import React, { useRef, useState } from "react";
import useEffectOnce from "@swiggy-private/dls-web-common/dist/hooks/useEffectOnce";
import useInterval from "@swiggy-private/dls-web-common/dist/hooks/useInterval";
import { Container, ScrollWrapper } from "./styles";

export interface IProps {
    children: React.ReactNode;
    onSlideChange?: (count: number) => void;
    scrollLengthPerUnit: number;
    totalCard: number;
    carouselHeight?: number;
    scrollDelay?: number;
    scrollTime?: number;
}

function VerticalScrollContainer({
    children,
    totalCard,
    onSlideChange,
    scrollLengthPerUnit,
    carouselHeight,
    scrollDelay = 3000, // in ms
    scrollTime = 600, // in ms
}: IProps) {
    const [activeSlideNumber, setActiveSlideNumber] = useState(1);
    const [transformYPosition, setTransformYPosition] = useState("0");
    const [transition, setTransition] = useState("none");

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const animationRefs = useRef({
        currentSlide: 1,
    });

    const incrementCardCount = (count: number): void => {
        let cardCount = count % totalCard;
        if (cardCount === 0) cardCount = totalCard;
        onSlideChange && onSlideChange(cardCount);
        setActiveSlideNumber(cardCount);
    };

    const slideToPosition = (translate: number, animation = false) => {
        setTransition(animation ? `transform ${scrollTime}ms ease-in-out` : "none");
        setTransformYPosition(`${translate}px`);
    };

    const getSlidePosition = (index: number) => -index * scrollLengthPerUnit;

    const handleTransitionend = (): void => {
        const { currentSlide } = animationRefs.current;
        if (currentSlide === totalCard + 1) {
            slideToPosition(getSlidePosition(1));
        }
    };

    const slideToCard = (index: number) => {
        const slidePosition = getSlidePosition(index);
        slideToPosition(slidePosition, true);
        animationRefs.current.currentSlide = index;
        incrementCardCount(index);
    };

    const addEventListeners = (): void => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.addEventListener("transitionend", handleTransitionend);
        }
    };

    const removeEventListeners = (): void => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.removeEventListener("transitionend", handleTransitionend);
        }
    };

    useEffectOnce(() => {
        addEventListeners();
        slideToCard(1);

        return () => {
            removeEventListeners();
        };
    });

    useInterval(() => {
        slideToCard(activeSlideNumber + 1);
    }, scrollDelay);

    return (
        <Container height={carouselHeight}>
            <ScrollWrapper transition={transition} transformY={transformYPosition} ref={scrollContainerRef}>
                {children}
            </ScrollWrapper>
        </Container>
    );
}

export default React.memo(VerticalScrollContainer);
